import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'context/AuthContext';
import { authService } from 'services/api';
import api from 'services/api';
import PoliticaCheckbox from 'components/PoliticaCheckbox';
import eventifyLogo from 'assets/eventify-logo.png';
import styles from './ChangePassword.module.css';

// bcrypt solo usa los primeros 72 bytes de la clave: el backend rechaza más.
const bytesUtf8 = (s) => new TextEncoder().encode(s).length;

export default function ChangePassword() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    current_password: '',
    new_password:     '',
    confirm_password: '',
  });
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');
  const [acepta,  setAcepta]  = useState(false);
  // El aviso va arriba y el botón abajo: en un celular de poca altura quedaba fuera de la vista. `intentos` lo trae a la vista
  // aunque un segundo intento repita el MISMO texto.
  const errorRef = useRef(null);
  const [intentos, setIntentos] = useState(0);
  useEffect(() => { if (error) errorRef.current?.scrollIntoView({ block: 'nearest' }); }, [error, intentos]);
  const [showPwd, setShowPwd] = useState({
    current: false, new: false, confirm: false,
  });

  // ── OTP state ─────────────────────────────────────────────────────────────
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpSent,     setOtpSent]     = useState(false);
  const [otpValue,    setOtpValue]    = useState('');
  const [otpSending,  setOtpSending]  = useState(false);
  const [otpLoading,  setOtpLoading]  = useState(false);
  const [countdown,   setCountdown]   = useState(0);

  const sendOtp = async () => {
    setOtpSending(true); setError('');
    try {
      await api.post('/auth/send-otp', { email: user?.email, motivo: 'cambio de contraseña' });
      setOtpSent(true);
      setCountdown(60);
      const timer = setInterval(() => {
        setCountdown(c => { if (c <= 1) { clearInterval(timer); return 0; } return c - 1; });
      }, 1000);
    } catch (err) {
      setError(err.response?.data?.message || 'Error al enviar el código');
    } finally { setOtpSending(false); }
  };

  const verifyOtp = async () => {
    if (otpValue.length !== 6) return setError('El código debe tener 6 dígitos');
    setOtpLoading(true); setError('');
    try {
      await api.post('/auth/verify-otp', { email: user?.email, otp: otpValue });
      setOtpVerified(true);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Código incorrecto');
    } finally { setOtpLoading(false); }
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggleShow = (k) => setShowPwd(s => ({ ...s, [k]: !s[k] }));

  // Si el correo es inválido/ficticio (o el OTP nunca llega por cualquier
  // razón), no había forma de salir de esta pantalla: GuestRoute manda
  // /login -> /dashboard porque ya hay sesión, y /dashboard manda de vuelta
  // aquí porque must_change_password sigue en true. Única salida real antes
  // de este fix: borrar el token a mano desde devtools.
  const backToLogin = async () => {
    await logout();
    navigate('/login');
  };

  // Validaciones de seguridad en tiempo real
  const pwdChecks = {
    length:   form.new_password.length >= 8,
    upper:    /[A-Z]/.test(form.new_password),
    lower:    /[a-z]/.test(form.new_password),
    number:   /[0-9]/.test(form.new_password),
    special:  /[!@#$%^&*]/.test(form.new_password),
    match:    form.new_password && form.new_password === form.confirm_password,
  };
  const pwdStrength = Object.values(pwdChecks).filter(Boolean).length;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIntentos(n => n + 1);

    // noValidate en el <form>: estas revisiones reemplazan el globo del navegador.
    if (!form.current_password) return setError('Ingresa la contraseña temporal actual');
    if (!pwdChecks.length)  return setError('La contraseña debe tener al menos 8 caracteres');
    if (!form.new_password.trim()) return setError('La contraseña no puede ser solo espacios');
    if (bytesUtf8(form.new_password) > 72) return setError('La contraseña es demasiado larga (máximo 72 caracteres sin tildes ni ñ)');
    if (!pwdChecks.match)   return setError('Las contraseñas no coinciden');
    if (!acepta) return setError('Debes aceptar la Política de Tratamiento de Datos Personales para activar tu cuenta');

    setLoading(true);
    try {
      await authService.changeTempPassword({ ...form, acepta_politica: acepta });
      await logout();
      navigate('/login', { state: { message: 'Contraseña actualizada. Por favor inicia sesión con tu nueva contraseña.' } });
    } catch (err) {
      // D2: el servidor exige el código verificado; si venció, se vuelve a pedir.
      if (err.response?.data?.code === 'OTP_NOT_VERIFIED') {
        setOtpVerified(false); setOtpSent(false); setOtpValue('');
      }
      setError(err.response?.data?.message || 'Error al cambiar la contraseña');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className={`auth-card ${styles.tarjeta}`}>

        {/* Logo */}
        <div className="auth-logo">
          <div className={styles.logoCaja}>
            <img src={eventifyLogo} alt="EVENTIFY" className={`auth-logo-img ${styles.logoImg}`} />
          </div>
        </div>

        {/* Banner informativo */}
        <div className={styles.banner}>
          <div className={styles.bannerIcono}>
            <i className={`fas fa-key ${styles.bannerGlifo}`} />
          </div>
          <div>
            <div className={styles.bannerTitulo}>
              Cambio de contraseña obligatorio
            </div>
            <div className={styles.bannerTexto}>
              Hola <strong className={styles.bannerNombre}>{user?.name}</strong>, estás usando una contraseña temporal.
              Por seguridad debes crear una nueva contraseña antes de continuar.
            </div>
          </div>
        </div>

        {error && (
          <div ref={errorRef} className={`alert alert-danger ${styles.alerta}`}>
            <i className={`fas fa-circle-exclamation ${styles.alertaIcono}`} />{error}
          </div>
        )}

        <div className={styles.volver}>
          <button type="button" onClick={backToLogin}
            className={styles.botonVolver}>
            <i className={`fas fa-arrow-left ${styles.iconoBtn}`} />
            Cerrar sesión y volver al login
          </button>
        </div>

        {/* ── Paso 1: Verificar OTP ─────────────────────────────────────── */}
        {!otpVerified && (
          <div>
            {!otpSent ? (
              <div className={styles.otpInicio}>
                <div className={styles.escudo}>
                  <i className={`fas fa-shield-halved ${styles.escudoGlifo}`} />
                </div>
                <p className={styles.otpTexto}>
                  Para continuar, enviaremos un código de verificación a<br />
                  <strong className={styles.correo}>{user?.email}</strong>
                </p>
                <button className={`btn btn-primary w-full ${styles.centrado}`}
                  onClick={sendOtp} disabled={otpSending}>
                  {otpSending
                    ? <><div className={`spinner ${styles.spinnerChico}`} /> Enviando...</>
                    : <><i className={`fas fa-paper-plane ${styles.iconoBtn}`} />Enviar código de verificación</>}
                </button>
              </div>
            ) : (
              <div>
                <div className={styles.otpEnviado}>
                  Código enviado a <strong className={styles.correo}>{user?.email}</strong>
                </div>
                <div className="form-group">
                  <label className="form-label">Código de verificación *</label>
                  <input className={`form-control ${styles.campoOtp}`}
                    value={otpValue}
                    onChange={e => setOtpValue(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000" maxLength={6}
                    autoFocus />
                  <div className={styles.ayuda}>Válido por 10 minutos</div>
                </div>
                <button className={`btn btn-primary w-full ${styles.centrado}`}
                  onClick={verifyOtp} disabled={otpLoading || otpValue.length !== 6}>
                  {otpLoading
                    ? <><div className={`spinner ${styles.spinnerChico}`} /> Verificando...</>
                    : <><i className={`fas fa-check ${styles.iconoBtn}`} />Confirmar código</>}
                </button>
                <div className={styles.reenvio}>
                  {countdown > 0
                    ? <span className={styles.cuenta}>Reenviar en {countdown}s</span>
                    : <button type="button" className="btn btn-secondary btn-sm" onClick={sendOtp} disabled={otpSending}>
                        <i className={`fas fa-rotate ${styles.iconoBtn}`} />Reenviar código
                      </button>}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Paso 2: Nueva contraseña (solo si OTP verificado) ─────────── */}
        {otpVerified && (
          <form onSubmit={handleSubmit} noValidate>

            {/* Contraseña temporal actual */}
            <div className="form-group">
              <label className="form-label">Contraseña temporal actual *</label>
              <div className={styles.campoConOjo}>
                <input
                  type={showPwd.current ? 'text' : 'password'}
                  className={`form-control ${styles.campoClave}`}
                  value={form.current_password}
                  onChange={e => set('current_password', e.target.value)}
                  placeholder="La que recibiste por email"
                  required
                />
                <button type="button" onClick={() => toggleShow('current')}
                  className={styles.ojo}>
                  <i className={`fas fa-eye${showPwd.current ? '-slash' : ''}`} />
                </button>
              </div>
            </div>

            {/* Nueva contraseña */}
            <div className="form-group">
              <label className="form-label">Nueva contraseña *</label>
              <div className={styles.campoConOjo}>
                <input
                  type={showPwd.new ? 'text' : 'password'}
                  className={`form-control ${styles.campoClave}`}
                  value={form.new_password}
                  onChange={e => set('new_password', e.target.value)}
                  placeholder="Mínimo 8 caracteres"
                  maxLength={72}
                  required
                />
                <button type="button" onClick={() => toggleShow('new')}
                  className={styles.ojo}>
                  <i className={`fas fa-eye${showPwd.new ? '-slash' : ''}`} />
                </button>
              </div>

              {/* Indicador de fortaleza */}
              {form.new_password && (
                <div className={styles.fortaleza}>
                  <div className={styles.barras}>
                    {[1,2,3,4,5].map(i => (
                      <div key={i} className={styles.barra} style={{
                        '--tono': i <= pwdStrength
                          ? pwdStrength <= 2 ? '#f04438' : pwdStrength <= 4 ? '#f79009' : '#12b76a'
                          : 'var(--border)',
                      }} />
                    ))}
                  </div>
                  <div className={styles.requisitos}>
                    {[
                      { key: 'length',  label: '8+ caracteres' },
                      { key: 'upper',   label: 'Mayúscula' },
                      { key: 'lower',   label: 'Minúscula' },
                      { key: 'number',  label: 'Número' },
                      { key: 'special', label: 'Carácter especial (!@#$%)' },
                    ].map(c => (
                      <div key={c.key} className={`${styles.requisito} ${pwdChecks[c.key] ? styles.requisitoOk : ''}`}>
                        <i className={`fas fa-${pwdChecks[c.key] ? 'check-circle' : 'circle'} ${styles.requisitoIcono}`} />
                        {c.label}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Confirmar contraseña */}
            <div className="form-group">
              <label className="form-label">Confirmar nueva contraseña *</label>
              <div className={styles.campoConOjo}>
                <input
                  type={showPwd.confirm ? 'text' : 'password'}
                  className={`form-control ${styles.campoClave} ${form.confirm_password ? (pwdChecks.match ? styles.confirmaOk : styles.confirmaMal) : ''}`}
                  value={form.confirm_password}
                  onChange={e => set('confirm_password', e.target.value)}
                  placeholder="Repite tu nueva contraseña"
                  maxLength={72}
                  required
                />
                <button type="button" onClick={() => toggleShow('confirm')}
                  className={styles.ojo}>
                  <i className={`fas fa-eye${showPwd.confirm ? '-slash' : ''}`} />
                </button>
              </div>
              {form.confirm_password && !pwdChecks.match && (
                <div className={styles.mensajeMal}>
                  <i className={`fas fa-times-circle ${styles.mensajeIcono}`} />Las contraseñas no coinciden
                </div>
              )}
              {form.confirm_password && pwdChecks.match && (
                <div className={styles.mensajeOk}>
                  <i className={`fas fa-check-circle ${styles.mensajeIcono}`} />Las contraseñas coinciden
                </div>
              )}
            </div>

            <PoliticaCheckbox checked={acepta} onChange={setAcepta} />

            <button type="submit" className={`btn btn-primary w-full ${styles.botonGuardar}`}
              disabled={loading || !pwdChecks.match || !pwdChecks.length}>
              {loading
                ? <><div className={`spinner ${styles.spinnerChico}`} /> Guardando...</>
                : <><i className={`fas fa-lock ${styles.iconoGuardar}`} />Guardar nueva contraseña</>
              }
            </button>
          </form>
        )}
      </div>
    </div>
  );
}